import { computed, unref, type ComputedRef, type Ref } from 'vue';

export interface ProductData {
    // For FIXED deposits this is a Money-like object from backend
    // For METERED deposits this will be null
    deposit_amount?: unknown | null;
    // For METERED deposits this represents the quantity (e.g. hours, boxes)
    // For FIXED deposits this will be null
    deposit_quantity?: number | null;
    deposit_type?: string;
    price_unit?: string;
    price_unit_formatted?: {
        label: string;
        unit_single: string;
        unit_plural: string;
    };
}

export interface PriceUnitFormatting {
    depositQuantity: ComputedRef<string | number>;
    depositUnitLabel: ComputedRef<string>;
}

export function usePriceUnitFormatting(
    productData: Ref<ProductData> | ComputedRef<ProductData> | ProductData,
): PriceUnitFormatting {
    const data = computed(() => {
        return unref(productData);
    });

    const depositQuantity = computed(() => {
        const product = data.value;

        // Only apply quantity/unit formatting for METERED deposits
        if (product?.deposit_type === 'METERED' && typeof product?.deposit_quantity === 'number') {
            const divisor = product.price_unit === 'HALF_HOUR' ? 2 : 1;
            return product.deposit_quantity / divisor;
        }
        return '-';
    });

    const depositUnitLabel = computed(() => {
        const product = data.value;

        // Only provide unit labels for METERED deposits
        if (product?.deposit_type !== 'METERED' || !product || !product.price_unit_formatted) return '';

        const { price_unit_formatted } = product;

        // Use the displayed quantity for pluralization instead of raw deposit_quantity
        const displayedQuantity = Number(depositQuantity.value);

        // Special handling for HALF_HOUR price unit
        if (product.price_unit === 'HALF_HOUR') {
            return displayedQuantity === 1 ? 'hour' : 'hours';
        }

        if (displayedQuantity === 1) {
            return price_unit_formatted.unit_single;
        }
        return price_unit_formatted.unit_plural;
    });

    return {
        depositQuantity,
        depositUnitLabel,
    };
}
